import React from 'react';
import Overlay from '../Overlay';
import BarcodeReader from 'react-barcode-reader';
import { ToastContainer, toast, ToastOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { sweetalert } from '../../App';
import { BatchSelect } from './selects/BatchSelect';
import Batch from '../../types/Batch';
import { WellPlateMap } from './WellPlateMap';
import BatchesAPI from '../../network/BatchesAPI';
import { WellSlot } from '../../types/WellSlot';
import { WellMapData } from './WellPlateReport';
import Select from 'react-select';
import { BinAPI } from '../../network/BinAPI';
import { BinType } from '../../types/BinType';
import { BatchManagementModal } from '../modals/BatchManagementModal';
import { getLabelWhenLabelAndValueAreSame, getNextWellSlot, getServiceReportType, hideModal, showModalNoOutsideClick, wellSlots, parseDate } from '../../util/FormatUtil';
import ReactToPrint from 'react-to-print';
import AdminAPI from '../../network/AdminAPI';
import SystemAPI from '../../network/SystemAPI';
import { AdminPages } from './AdminNav';
import { isBlank, isOnlyWhitespace } from '../../util/ValidationUtil';
import ServicesAPI from '../../network/ServicesAPI';
import SamplesAPI from '../../network/SamplesAPI';

let audioSuccess = new Audio('/sound/Success.wav');
let audioError = new Audio('/sound/Beep.wav');

const inputModes = [
  { label: 'Keyboard', value: 'keyboard' },
  { label: 'Scanner', value: 'scanner' },
];

// pre-printed internal labels are an 'A' followed by 8 digits. only used to validate an
// internal ID, never to decide whether an input is one - specimen IDs have no fixed format
const internalIdRegex = /^A\d{8}$/;

interface AccessionState {
  showLoading?;
  specimenUpdate: boolean;
  internalSpUpdate: boolean;
  selectedBatch: Batch;
  plateData: WellMapData[];
  slotMap: WellSlot[];
  selectedBin: { label; value: BinType };
  bins: BinType[];
  scannedSpecimenID: string;
  scannedInternalID: string;
  batches: Batch[];
  disabledScan?: boolean;
  collectionDateWarning?: string;
  pageCustomQuestions?;
  hasResult: boolean;
  inputMode;
  specimenExists: boolean;
  starterWellSlot: string;
  services;
  collectionDate?;
  showCollectionDate: boolean;
  validCollectionDate: boolean;
}

export class AccessionApp extends React.Component<{}, AccessionState> {
  trigger: boolean;
  // bumped by clear() so a response from a scan the operator abandoned is ignored
  scanSequence: number = 0;
  private componentRef: any;
  toastOptions: ToastOptions = {
    position: 'top-center',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      showLoading: true,
      specimenUpdate: false,
      internalSpUpdate: true,
      selectedBatch: null,
      plateData: [],
      slotMap: [],
      bins: [],
      selectedBin: null,
      scannedSpecimenID: '',
      scannedInternalID: '',
      batches: [],
      collectionDateWarning: '',
      pageCustomQuestions: [],
      hasResult: false,
      inputMode: null,
      specimenExists: false,
      starterWellSlot: '',
      services: [],
      showCollectionDate: false,
      collectionDate: null,
      validCollectionDate: false,
    };
    this.handleInput = this.handleInput.bind(this);
    this.clear = this.clear.bind(this);
  }

  componentDidMount() {
    this.setState({ showLoading: true }, () => {
      SystemAPI.getCustomQuestions(AdminPages.BatchManagement).then(res => {
        this.setState({ pageCustomQuestions: res.questions });
      });
      BinAPI.getAllBins().then(data => {
        this.setState({ bins: data });
      });
      BatchesAPI.getAllBatches().then(data => {
        this.setState({ batches: data, showLoading: false });
      });
      BatchesAPI.getCollectionDateWarningSetting().then(data => {
        this.setState({ collectionDateWarning: data.warningMessage });
      });
      ServicesAPI.getAllServicesForMachineExport().then(data => {
        this.setState({ services: data.data });
      });
    });
  }

  async handleInput(input, source?: 'specimen' | 'internal') {
    if (isBlank(input) || isOnlyWhitespace(input)) {
      return;
    }
    const scanSequence = this.scanSequence;

    // if error on scan, close popup when we scan again
    if (sweetalert.isVisible()) {
      sweetalert.close();
    }
    if (this.trigger || this.state.disabledScan) {
      audioError.play();
      return sweetalert.fire({
        icon: 'error',
        title: '',
        text: 'Please wait for the server the process your request',
      });
    }
    if (!this.state.selectedBatch) {
      return sweetalert.fire({
        icon: 'error',
        title: '',
        text: 'Please select or create a new batch.',
      });
    }
    if (!this.state.selectedBin && !input.startsWith('BC')) {
      return sweetalert.fire({
        icon: 'error',
        title: '',
        text: 'Please select or scan the Bin that this specimen came from.',
      });
    }
    if (input.startsWith('BC')) {
      let bin = this.state.bins.find(b => b.BinID === input);
      if (bin) {
        this.setState({ selectedBin: { label: bin.BinID, value: bin } });
        audioSuccess.play();
        toast.success('Bin Set', this.toastOptions);
        return;
      }
      audioError.play();
      toast.error('No bin found', this.toastOptions);
      return;
    }

    const isOraPathService = getServiceReportType(this.state.selectedBatch.ServiceID, this.state.services) > 1 ? true : false;

    if (input === 'collectionDate') {
      if (this.state.collectionDate) {
        if (isNaN(Date.parse(this.state.collectionDate))) {
          audioError.play();
          return sweetalert.fire({
            icon: 'error',
            title: '',
            text: `Collection Date must be a valid date`,
          });
        } else if (this.state.collectionDate > new Date(new Date().setHours(0, 0, 0, 0))) {
          audioError.play();
          return sweetalert.fire({
            icon: 'error',
            title: '',
            text: `Collection Date cannot be in the future`,
          });
        } else {
          audioSuccess.play();
          this.setState({ validCollectionDate: true });
          return sweetalert.fire({
            icon: 'success',
            title: '',
            html: '<p>Collection Date complete</p><p>Please enter Internal ID</p>',
          });
        }
      } else {
        audioError.play();
        return sweetalert.fire({
          icon: 'error',
          title: '',
          text: `Collection Date must be a valid date`,
        });
      }
    }

    // the keyboard fields know which value they hold; the scanner does not, so fall back to
    // where we are in the workflow - specimen ID first, then internal ID
    const expectingInternalID = source ? source === 'internal' : this.state.scannedSpecimenID && this.state.specimenExists ? true : false;

    if (expectingInternalID) {
      if (!this.state.scannedSpecimenID) {
        audioError.play();
        return sweetalert.fire({
          icon: 'error',
          title: '',
          text: 'You cannot enter Internal Specimen ID without entering Specimen ID.',
        });
      }

      if (!internalIdRegex.test(input)) {
        audioError.play();
        return sweetalert.fire({
          icon: 'error',
          title: '',
          html: `<p>Improperly formatted Internal ID</p><p>Internal ID should start with an 'A' followed by 8 numbers</p>`,
        });
      }

      if (isOraPathService) {
        if (!this.state.collectionDate) {
          audioError.play();
          return sweetalert.fire({
            icon: 'error',
            title: '',
            text: 'Collection Date is required before entering the Internal ID.',
          });
        }
        if (this.state.collectionDate && isNaN(Date.parse(this.state.collectionDate))) {
          audioError.play();
          return sweetalert.fire({
            icon: 'error',
            title: '',
            text: `Collection Date must be a valid date`,
          });
        } else if (this.state.collectionDate && this.state.collectionDate > new Date(new Date().setHours(0, 0, 0, 0))) {
          audioError.play();
          return sweetalert.fire({
            icon: 'error',
            title: '',
            text: `Collection Date cannot be in the future`,
          });
        }
      }

      this.trigger = true;
      // claim the well slot up front - it used to advance in the callback, which let a second
      // accession started in the meantime target the same slot
      const accessionWellSlot = this.state.starterWellSlot;
      this.setState({ showLoading: true, scannedInternalID: input, starterWellSlot: getNextWellSlot(accessionWellSlot, this.state.slotMap) }, async () => {
        let res = await BatchesAPI.accessionSample({
          specimenID: this.state.scannedSpecimenID.trim(),
          internalSpecimenID: this.state.scannedInternalID.trim(),
          sampleReceivingID: this.state.selectedBin.value.ID,
          batchID: this.state.selectedBatch.ID,
          selectedWellSlot: accessionWellSlot,
          collectionDate: this.state.collectionDate,
          isPerioService: getServiceReportType(this.state.selectedBatch.ServiceID, this.state.services) > 1 ? true : false,
        });
        this.setState({
          showLoading: false,
          scannedSpecimenID: '',
          scannedInternalID: '',
        });
        this.trigger = false;
        if (!res.success) {
          audioError.play();
          this.setState({
            specimenExists: false,
            showCollectionDate: false,
            collectionDate: null,
            validCollectionDate: false,
          });
          return sweetalert.fire({
            icon: 'error',
            title: '',
            text: res.reason,
          });
        } else {
          audioSuccess.play();
          toast.success('Sample Accessioned', this.toastOptions);
          this.setState({
            specimenExists: false,
            showCollectionDate: false,
            collectionDate: null,
            validCollectionDate: false,
          });
          this.retrieve();
        }
      });
    } else {
      let hasExistingCollectionDate = false;
      this.trigger = true;
      this.setState({ showLoading: true, disabledScan: true });
      if (input !== 'collectionDate') {
        let checkResponse = await BatchesAPI.precheckSpecimen(input, this.state.selectedBatch.ID, isOraPathService);
        this.trigger = false;
        this.setState({ showLoading: false, disabledScan: false });
        if (scanSequence !== this.scanSequence) {
          return;
        }
        if (!checkResponse.success) {
          sweetalert.fire({
            icon: 'error',
            title: '',
            text: internalIdRegex.test(input) ? `${checkResponse.reason} If this is an Internal ID, scan the Specimen ID first.` : checkResponse.reason,
          });
          return;
        }
        if (checkResponse.isOldSample) {
          audioError.play();
          let result = await sweetalert.fire({
            icon: 'error',
            title: '',
            text: this.state.collectionDateWarning,
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
            showCancelButton: true,
          });
          if (!result.isConfirmed) {
            return;
          }
        }
        if (isOraPathService) {
          // the sample may already have a collection date (i.e. a DTC sample the patient registered)
          let existingCollectionDate = null;
          try {
            let sampleResponse = await SamplesAPI.getSamplesBySpecimenId(input);
            let sample = Array.isArray(sampleResponse?.response) ? sampleResponse.response[0] : sampleResponse?.response;
            if (sample && sample.CollectionDate) {
              existingCollectionDate = sample.CollectionDate;
            }
          } catch (e) {
            existingCollectionDate = null;
          }
          if (scanSequence !== this.scanSequence) {
            return;
          }
          hasExistingCollectionDate = existingCollectionDate ? true : false;
          // always set, so a previous specimen's date is never carried over to this one
          this.setState({
            collectionDate: existingCollectionDate,
            validCollectionDate: hasExistingCollectionDate,
          });
        }
        if (checkResponse.rerunCollectionDate) {
          this.setState({
            collectionDate: checkResponse.rerunCollectionDate,
            validCollectionDate: true,
          });
        }
      }

      if (scanSequence !== this.scanSequence) {
        return;
      }
      // commit before the alert - a scan cannot dismiss it, so the internal ID that follows
      // must already see a scanned specimen or it gets routed as a specimen ID
      this.setState({
        specimenExists: true,
        scannedSpecimenID: input,
        showCollectionDate: isOraPathService ? true : this.state.showCollectionDate,
      });
      audioSuccess.play();
      return sweetalert.fire({
        icon: 'success',
        title: '',
        html:
          isOraPathService && !hasExistingCollectionDate
            ? '<p>Specimen ID validated successfully</p><p>Please enter Collection Date</p>'
            : '<p>Specimen ID validated successfully</p><p>Please enter Internal ID</p>',
      });
    }
  }

  getStatus() {
    if (!this.state.selectedBatch) {
      return 'Please select or create a new batch.';
    } else if (!this.state.selectedBin) {
      return 'Please select a bin.';
    } else if (!this.state.scannedSpecimenID) {
      return 'Please scan or enter a specimen ID.';
    } else if (!this.state.scannedInternalID) {
      return 'Please scan or enter an internal specimen ID.';
    }
    return '';
  }

  async retrieve() {
    this.setState({ showLoading: true });
    let starterWellSlot = '';
    let response = await BatchesAPI.getBatchMap(this.state.selectedBatch.ID);

    if (!response.success) {
      sweetalert.fire({ icon: 'error', title: '', text: response.reason });
      this.setState({
        plateData: [],
      });
    } else {
      if (response.hasResult) {
        sweetalert.fire({
          title: 'Batch has results!',
          text: 'Table is read-only',
          icon: 'info',
        });
      }
      let filledWellSlotArray = [];
      if (response.data && response.data.length > 0) {
        response.data.map(d => {
          filledWellSlotArray.push(d.WellSlot);
        });
      }
      response.wellSlots.map(ws => {
        if (ws.IsBlocked) {
          filledWellSlotArray.push(ws.WellSlot);
        }
      });

      for (let i = 0; i < response.wellSlots.length; i++) {
        const wellSlot = response.wellSlots[i].WellSlot;
        if (filledWellSlotArray.includes(wellSlot)) {
          continue;
        } else {
          starterWellSlot = wellSlot;
          break;
        }
      }

      this.setState({
        plateData: response.data,
        slotMap: response.wellSlots,
        hasResult: response.hasResult,
        starterWellSlot: starterWellSlot,
      });
    }
    this.setState({ showLoading: false });
  }

  createNewBatch(batch) {
    hideModal(BatchManagementModal.ID);
    this.setState({ showLoading: true }, async () => {
      let response = await BatchesAPI.createBatch(batch);
      if (!response.success) {
        this.setState({ showLoading: false });
        return sweetalert.fire({
          title: '',
          text: response.reason,
          icon: 'error',
        });
      } else {
        this.setState({ selectedBatch: null });
        sweetalert.fire({ title: '', text: 'Batch saved', icon: 'success' });
      }
      let batches = await BatchesAPI.getAllBatches();
      this.setState(
        {
          showLoading: false,
          batches: batches,
          selectedBatch: batches.find(b => b.BatchNum === batch.BatchNum),
        },
        () => {
          this.retrieve();
        }
      );
    });
  }

  clear() {
    this.scanSequence++;
    this.trigger = false;
    this.setState({
      showLoading: false,
      specimenUpdate: false,
      internalSpUpdate: true,
      scannedSpecimenID: '',
      scannedInternalID: '',
      specimenExists: false,
      showCollectionDate: false,
      collectionDate: null,
      validCollectionDate: false,
      disabledScan: false,
    });
  }

  getAvailableSlots() {
    let slotMapCopy = JSON.parse(JSON.stringify(this.state.slotMap));
    let availableSlots = slotMapCopy.filter(sm => !sm.BlockedText);
    availableSlots = availableSlots.map(s => {
      return { label: s.WellSlot, value: s.WellSlot };
    });
    if (this.state.plateData && this.state.plateData.length > 0) {
      this.state.plateData.map(pd => {
        let indexOfPopulatedWellSlot = availableSlots.findIndex(f => f.value === pd.WellSlot);
        availableSlots.splice(indexOfPopulatedWellSlot, 1);
      });
    }
    return availableSlots;
  }

  async clearWellSlot(wellSlotInfo) {
    this.setState({ showLoading: true }, async () => {
      if (!wellSlotInfo) {
        await this.retrieve();
      }
      let response = await BatchesAPI.clearWellSlot({
        specimenID: wellSlotInfo.SpecimenID,
        batch: this.state.selectedBatch.ID,
      });
      if (!response.success) {
        sweetalert.fire({ title: '', text: response.reason, icon: 'error' });
      } else {
        await this.retrieve();
        sweetalert.fire({
          title: '',
          text: 'Well Slot cleared',
          icon: 'success',
        });
      }
      this.setState({ showLoading: false });
    });
  }

  render() {
    let service =
      this.state.services && this.state.selectedBatch && this.state.selectedBatch.ServiceID ? this.state.services.find(f => f.ID === this.state.selectedBatch.ServiceID) : null;
    let isPerio = false;
    if (service && service.ReportType > 1) {
      isPerio = true;
    }

    return (
      <React.Fragment>
        <Overlay show_loading={this.state.showLoading} />
        <ToastContainer />
        <BarcodeReader onError={err => console.error(err)} onScan={data => this.handleInput(data)} />
        <BatchManagementModal selectedBatch={null} onSubmit={batch => this.createNewBatch(batch)} pageCustomQuestions={this.state.pageCustomQuestions} />
        <div className="container-fluid  min-vh-100 ">
          <div className={'row'}>
            <div className="col-xl-6 col-lg-6 col-md-12 col-12 pt-2">
              <div className="card">
                <div className="card-header verlag-bold">
                  <h4>Accessioning</h4>
                </div>
                <div className="card-body">
                  <BatchSelect
                    batches={this.state.batches}
                    selectedBatch={this.state.selectedBatch}
                    onSelected={(batch: { label; value: Batch }) =>
                      this.setState({ selectedBatch: batch.value }, () => {
                        this.retrieve();
                      })
                    }
                    value={this.state.selectedBatch ? 1 : null}
                  />

                  <div className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={'Lists the bin IDs'}>
                    <label id={'Bin'} htmlFor={'Bin'} className="col-sm-4 col-form-label">
                      Bin:
                    </label>
                    <div className="col-sm-8 p-0 m-0">
                      <Select
                        id={'Bin'}
                        isSearchable={true}
                        placeholder={'Please Select...'}
                        noOptionsMessage={() => 'No option'}
                        isDisabled={!this.state.selectedBatch || this.state.hasResult}
                        value={this.state.selectedBin}
                        onChange={e => this.setState({ selectedBin: e })}
                        className={'state_select'}
                        options={this.state.bins.map(b => ({
                          label: b.BinID,
                          value: b,
                        }))}
                      />
                    </div>
                  </div>

                  <div className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={'Input Mode'}>
                    <label id={'InputMode'} htmlFor={'Bin'} className="col-sm-4 col-form-label">
                      Input Mode:
                    </label>
                    <div className="col-sm-8 p-0 m-0">
                      <Select
                        id={'InputMode'}
                        isSearchable={true}
                        placeholder={'Please Select...'}
                        noOptionsMessage={() => 'No option'}
                        isDisabled={!this.state.selectedBatch || this.state.hasResult}
                        value={this.state.inputMode}
                        onChange={e =>
                          this.setState({
                            inputMode: e,
                            scannedInternalID: '',
                            scannedSpecimenID: '',
                          })
                        }
                        className={'state_select'}
                        options={inputModes}
                      />
                    </div>
                  </div>

                  <div className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={'Selected Well Slot'}>
                    <label id={'SelectedWellSlot'} htmlFor={'Bin'} className="col-sm-4 col-form-label">
                      WellSlot:
                    </label>
                    <div className="col-sm-8 p-0 m-0">
                      <Select
                        id={'SelectedWellSlot'}
                        isSearchable={true}
                        placeholder={'Please Select...'}
                        noOptionsMessage={() => 'No option'}
                        isDisabled={!this.state.selectedBatch || this.state.hasResult}
                        value={getLabelWhenLabelAndValueAreSame(this.state.starterWellSlot, wellSlots)}
                        onChange={e =>
                          this.setState({
                            starterWellSlot: e.value,
                            scannedInternalID: '',
                            scannedSpecimenID: '',
                          })
                        }
                        className={'state_select'}
                        options={this.getAvailableSlots()}
                      />
                    </div>
                  </div>

                  <div className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={'Scanned ID'}>
                    <label id={'BarcodeScan'} htmlFor={'BarcodeScan'} className="col-sm-4 col-form-label">
                      Specimen ID:
                    </label>
                    <div className="col-sm-8  p-0 m-0">
                      <input
                        disabled={!this.state.inputMode || this.state.hasResult}
                        id={'BarcodeScan'}
                        type="search"
                        className={'form-control'}
                        value={this.state.scannedSpecimenID}
                        readOnly={(this.state.inputMode && this.state.inputMode.value === 'scanner') || this.state.specimenExists ? true : false}
                        onChange={e => {
                          this.setState({
                            scannedSpecimenID: e.target.value.trim(),
                          });
                        }}
                        onBlur={e => {
                          this.setState({ scannedSpecimenID: e.target.value.trim() }, () => {
                            this.handleInput(e.target.value, 'specimen');
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className={this.state.showCollectionDate ? 'form-group row' : 'd-none'} data-toggle={'tooltip'} data-placement={'top'} title={'Collection Date'}>
                    <label id={'CollectionDateLabel'} htmlFor={'CollectionDateLabel'} className="col-sm-4 col-form-label">
                      Collection Date
                    </label>
                    <div className="col-sm-8 p-0 m-0">
                      <input
                        disabled={!this.state.selectedBatch || this.state.hasResult || this.state.validCollectionDate}
                        id={'CollectionDate'}
                        type="date"
                        className={'form-control'}
                        value={this.state.collectionDate ? new Date(this.state.collectionDate).toISOString().split('T')[0] : ''}
                        onChange={e => {
                          const inputValue = e.target.value;
                          const [year, month, day] = inputValue.split('-');
                          if (Date.parse(inputValue)) {
                            if (year && year.length > 4) {
                              const correctedYear = year.slice(0, 4);
                              const newDateValue = `${correctedYear}-${month}-${day}`;
                              this.setState({
                                collectionDate: new Date(newDateValue),
                              });
                            } else {
                              this.setState({
                                collectionDate: new Date(inputValue),
                              });
                            }
                          }
                        }}
                        onBlur={e => {
                          this.handleInput('collectionDate');
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-group row" data-toggle={'tooltip'} data-placement={'top'} title={'Scanned Internal ID'}>
                    <label id={'InternalIDScanLabel'} htmlFor={'InternalIDScanLabel'} className="col-sm-4 col-form-label">
                      Internal ID:
                    </label>
                    <div className="col-sm-8  p-0 m-0">
                      <input
                        disabled={!this.state.inputMode || this.state.hasResult}
                        id={'InternalIDScan'}
                        type="search"
                        className={'form-control'}
                        value={this.state.scannedInternalID}
                        readOnly={
                          (this.state.inputMode && this.state.inputMode.value === 'scanner') || !this.state.specimenExists || (isPerio && !this.state.validCollectionDate)
                            ? true
                            : false
                        }
                        onChange={e => {
                          this.setState({ scannedInternalID: e.target.value });
                        }}
                        onBlur={e => {
                          this.setState({ scannedInternalID: e.target.value }, () => {
                            this.handleInput(e.target.value, 'internal');
                          });
                        }}
                      />
                    </div>
                  </div>

                  <p>Status: {this.getStatus()} </p>
                </div>
                <div className={'card-footer'}>
                  <button
                    className={'btn immySubmitButtonOutline'}
                    onClick={() => {
                      showModalNoOutsideClick(BatchManagementModal.ID);
                    }}
                  >
                    Create New Batch
                  </button>

                  {this.state.selectedBatch && (
                    <ReactToPrint trigger={() => <button className={'btn btn-outline-success float-right'}>Print</button>} content={() => this.componentRef} />
                  )}
                  <button
                    className={
                      this.state.inputMode && this.state.inputMode.value === 'keyboard' && this.state.scannedInternalID && this.state.scannedInternalID.length > 0
                        ? 'btn btn-outline-success float-right mr-3 visible'
                        : 'invisible'
                    }
                    onClick={() => this.handleInput(this.state.scannedInternalID, 'internal')}
                  >
                    Add Sample
                  </button>
                  {(this.state.scannedSpecimenID || this.state.specimenExists) && (
                    <button className={'btn btn-outline-danger float-right mr-3'} onMouseDown={e => e.preventDefault()} onClick={() => this.clear()}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
            {this.state.selectedBatch && (
              <WellPlateMap
                isPerio={isPerio}
                isComprehensive={service.ReportType >= 4 ? true : false}
                hasResult={this.state.hasResult}
                clearWellSlot={e => this.clearWellSlot(e)}
                starterWellSlot={this.state.starterWellSlot ? this.state.starterWellSlot : null}
                onCellChange={e => this.setState({ starterWellSlot: e })}
                batch={this.state.selectedBatch}
                ref={el => (this.componentRef = el)}
                batchNum={this.state.selectedBatch ? this.state.selectedBatch.BatchNum : ''}
                plateData={this.state.plateData}
                slotMap={this.state.slotMap}
              />
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}
